import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject, linkedSignal, signal } from '@angular/core';
import { FormField, email, form, pattern, required } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';

import { finalize } from 'rxjs';

import { Agreement } from '../schemas';
import { Auth, Lambda, Messenger } from '../service';

export interface ExportDocumentationData {
  entity: Agreement;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'lcm-export-documentation-form',
  templateUrl: './export-documentation-form.html',
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressBarModule,
    MatInputModule,
    MatCheckboxModule,
    FormField,
  ],
})
export class ExportDocumentationForm {
  private auth = inject(Auth);
  private http = inject(HttpClient);
  private messenger = inject(Messenger);
  private data = inject(MAT_DIALOG_DATA) as ExportDocumentationData;
  private lambda = inject(Lambda);

  private dialog = inject(MatDialogRef);

  private preparedBy = computed(() => this.auth.name());
  private email = computed(() => this.auth.email());

  private model = linkedSignal(() => ({
    preparedBy: this.preparedBy(),
    userEmail: this.email(),
    userPhone: '+1 (343) 8833051',
    recipient: this.email(),
    includeComments: true,
    includeImages: false,
    addDraftWatermark: false,
    version: '1.0',
    jqlExpression: '',
  }));

  pending = signal(false);

  form = form(this.model, (schema) => {
    required(schema.preparedBy);
    required(schema.userEmail);
    email(schema.userEmail);
    required(schema.userPhone);
    required(schema.recipient);
    email(schema.recipient);

    required(schema.version);
    pattern(schema.version, /^\d+(\.\d+)?$/);
  });

  async save() {
    const data = this.form().value();
    this.pending.set(true);
    this.http
      .post(this.lambda.getProjectDocumentationExportUrl(this.data.entity.JiraTitle), data)
      .pipe(finalize(() => this.pending.set(false)))
      .subscribe({
        next: () => {
          this.messenger.info("Documentation export started successfully. You'll receive an email when it is ready.");
          this.dialog.close();
        },
        error: this.messenger.handleHttpError,
      });
  }
}
